import React, { useEffect, useState } from "react";

import {
    useTable,
    useSortBy,
    usePagination,
    useExpanded,
    useFlexLayout,
} from "react-table";
import { useSticky } from "react-table-sticky";
import PerfectScrollbar from "react-perfect-scrollbar";
import Header from "./Header";
import Row from "./Row";
import Pagination from "./Pagination";
import PropTypes from "prop-types";

function Table(props) {
    const {
        columns,
        data: initialData,
        onSort = null,
        getTrClasses = null,
        renderRowSubComponent = null,
        manualSortBy = false,
        sticky = false,
        pageStickyHeader = false,
        defaultPageSize = 5,
        striped = true,
        bordered = true,
        pagination = true,
        height = "auto",
        note = "",
        className = "",
        baseClassName = "",
        paginationClassName = "",
        print = false,
        fieldName,
        selectedValue,
        pageName,
        responsiveConfig = {
            type: "COLUMN",
            striped: false,
            screen: "md",
            bordered: true,
        },
        style,
        displayReportView,
        defaultExpandAll = false,
        options,
    } = props;

    const defaultColumn = React.useMemo(
        () => ({
            width: 150,
        }),
        []
    );

    const [data, setData] = useState([]);

    useEffect(() => {
        setData(initialData);
    }, [initialData]);

    const {
        getTableProps,
        getTableBodyProps,
        prepareRow,
        headerGroups,
        rows,
        page,
        pageCount,
        gotoPage,
        setPageSize,
        toggleRowExpanded,
        toggleAllRowsExpanded,
        state: { pageSize, sortBy },
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            initialState: { pageIndex: 0, pageSize: defaultPageSize },
            manualSortBy: manualSortBy,
        },
        useFlexLayout,
        useSticky,
        useSortBy,
        useExpanded,
        usePagination
    );

    useEffect(() => {
        if (onSort) onSort(sortBy);
    }, [onSort, sortBy]);

    useEffect(() => {
        if (defaultExpandAll) toggleAllRowsExpanded(true);
    }, [defaultExpandAll, data, toggleAllRowsExpanded]);

    const _getTrClasses = (row) => {
        if (getTrClasses) return getTrClasses(row);
        return "tr";
    };

    const HEIGHT_FOR_RECORDS = 10;

    const tableData = pagination === true ? page : rows;
    const varBorder = bordered === true ? "bordered " : "borderless ";
    const varStrip = striped === true ? "striped " : "solid ";
    const scrollCondClass =
        pagination === true ? "" : "table-body-scroll stop-scroll-chaining";
    const responsiveBorder =
        responsiveConfig.bordered === true
            ? "responsive-bordered "
            : "responsive-borderless ";
    const responsiveStrip =
        responsiveConfig.striped === true
            ? " responsive-striped "
            : " responsive-solid ";

    const renderStickyRows = () =>
        tableData.map((row, i) => {
            prepareRow(row);
            const trClassName = _getTrClasses(row);

            return (
                <Row
                    key={i}
                    row={row}
                    toggleRowExpanded={null}
                    trClassName={trClassName}
                    print={print}
                    renderRowSubComponent={renderRowSubComponent}
                    responsiveConfig={responsiveConfig}
                />
            );
        });

    const renderStickyTable = () => {
        if (pageStickyHeader === true) {
            // Header rendered outside PerfectScrollbar's overflow:hidden
            // wrapper so `position: sticky` on the header (see CSS for
            // baseClassName + " .header") sticks relative to the page
            // viewport instead of being trapped by PerfectScrollbar's
            // scroll container.
            return (
                <div
                    {...getTableProps()}
                    className={
                        "sticky-table page-sticky-header " + baseClassName
                    }
                >
                    <Header headerGroups={headerGroups} style={style} />

                    <PerfectScrollbar
                        {...getTableBodyProps()}
                        className={
                            "table mb-0 sticky custom-scrollbar body " +
                            varStrip +
                            varBorder +
                            responsiveBorder +
                            className
                        }
                    >
                        {renderStickyRows()}
                    </PerfectScrollbar>
                </div>
            );
        }

        return (
            <div className={"sticky-table " + baseClassName}>
                <PerfectScrollbar
                    {...getTableProps()}
                    className={
                        "table mb-0 sticky custom-scrollbar " +
                        varStrip +
                        varBorder +
                        responsiveBorder +
                        className
                    }
                >
                    <Header headerGroups={headerGroups} style={style} />

                    <div {...getTableBodyProps()} className="body">
                        {renderStickyRows()}
                    </div>
                </PerfectScrollbar>
            </div>
        );
    };

    return (
        <>
            {sticky === true ? (
                renderStickyTable()
            ) : (
                <>
                    <div
                        className={
                            "base-table  " +
                            `responsive-${responsiveConfig.screen} ` +
                            responsiveStrip +
                            responsiveBorder +
                            baseClassName
                        }
                    >
                        <PerfectScrollbar
                            {...getTableProps()}
                            className={
                                "table mb-0 " + varStrip + varBorder + className
                            }
                        >
                            <Header
                                headerGroups={headerGroups}
                                style={style}
                                displayReportView={displayReportView}
                            />

                            <PerfectScrollbar
                                {...getTableBodyProps()}
                                className={"body " + scrollCondClass}
                                style={{
                                    height:
                                        pagination === true && page?.length > 9
                                            ? Math.min(
                                                  height,
                                                  tableData.length *
                                                      (height /
                                                          HEIGHT_FOR_RECORDS)
                                              )
                                            : "auto",
                                }}
                            >
                                {tableData.map((row, i) => {
                                    prepareRow(row);

                                    const trClassName = _getTrClasses(row);

                                    return (
                                        <Row
                                            key={i}
                                            row={row}
                                            toggleRowExpanded={
                                                toggleRowExpanded
                                            }
                                            trClassName={trClassName}
                                            renderRowSubComponent={
                                                renderRowSubComponent
                                            }
                                            print={print}
                                            responsiveConfig={responsiveConfig}
                                            displayReportView={
                                                displayReportView
                                            }
                                        />
                                    );
                                })}
                            </PerfectScrollbar>
                        </PerfectScrollbar>
                    </div>
                </>
            )}

            {note !== "" && (
                <p className="px-20p fs-xs text-semibold">{note}</p>
            )}

            {pagination === true && (
                <>
                    <Pagination
                        pageCount={pageCount}
                        gotoPage={gotoPage}
                        setPageSize={setPageSize}
                        pageSize={pageSize}
                        paginationClassName={paginationClassName}
                        fieldName={fieldName}
                        selectedValue={selectedValue}
                        pageName={pageName}
                        options={options}
                    />
                </>
            )}
        </>
    );
}

Table.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            Header: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
                .isRequired,
            accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
            width: PropTypes.number,
            Cell: PropTypes.func,
        })
    ).isRequired,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    onSort: PropTypes.func,
    getTrClasses: PropTypes.func,
    renderRowSubComponent: PropTypes.func,
    manualSortBy: PropTypes.bool,
    sticky: PropTypes.bool,
    pageStickyHeader: PropTypes.bool,
    defaultPageSize: PropTypes.number,
    striped: PropTypes.bool,
    bordered: PropTypes.bool,
    pagination: PropTypes.bool,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    note: PropTypes.string,
    className: PropTypes.string,
    baseClassName: PropTypes.string,
    paginationClassName: PropTypes.string,
    print: PropTypes.bool,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
        })
    ),
    fieldName: PropTypes.string,
    selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pageName: PropTypes.string,
    responsiveConfig: PropTypes.shape({
        type: PropTypes.oneOf(["COLUMN", "ROW", ""]),
        striped: PropTypes.bool,
        screen: PropTypes.string,
        bordered: PropTypes.bool,
    }),
    style: PropTypes.string,
    displayReportView: PropTypes.bool,
    defaultExpandAll: PropTypes.bool,
};

export default Table;
